"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Upload } from "lucide-react";
import { trackFileUploaded, type UploadSurface } from "../../lib/utils/analytics";

interface UploadCtaProps {
  /** GA `surface` param for file_uploaded (which landing sent the file). */
  surface?: UploadSurface;
  /** Where to land with the file loaded (e.g. "/?view=preview" for viewer). */
  to?: string;
  /** Sub-line under the drop headline. */
  hint?: string;
}

/**
 * Landing-page CTA: pick (or drop) a Markdown file and land in the converter
 * with it already loaded. Hands off via localStorage — the converter restores
 * `markdownContent` on mount.
 */
export default function UploadCta({
  surface = "landing_markdown_to_word",
  to = "/",
  hint = "— .md, .markdown, .txt · opens in the converter with a live preview",
}: UploadCtaProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const router = useRouter();

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      if (!text) return;
      localStorage.setItem("markdownContent", text);
      trackFileUploaded(text.length, surface);
      router.push(to);
    };
    reader.readAsText(file);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      className={`mx-auto max-w-[560px] cursor-pointer rounded-[12px] border-[1.5px] border-dashed px-6 py-10 text-center transition-colors duration-150 ${
        dragOver ? "border-accent bg-surface" : "border-dash bg-surface hover:border-accent"
      }`}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".md,.markdown,.txt,text/markdown,text/plain"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Upload className="mx-auto h-6 w-6 text-muted" strokeWidth={1.5} />
      <p className="m-0 mt-2.5 font-display text-[22px] font-semibold text-ink">
        Drop your Markdown file here
      </p>
      <p className="m-0 mt-1 text-[13.5px] text-ink-3">
        or{" "}
        <span className="text-accent underline decoration-accent/30 underline-offset-2">
          browse
        </span>{" "}
        {hint}
      </p>
    </div>
  );
}
